// Messaging contract shared by the API and the web app (pilot slice 8, ADR-0034).
import type { Role } from './auth';

export const MESSAGE_CHANNELS = ['WHATSAPP', 'SMS', 'EMAIL'] as const;
export type MessageChannel = (typeof MESSAGE_CHANNELS)[number];

export const MESSAGE_STATUSES = ['QUEUED', 'SENDING', 'SENT', 'DELIVERED', 'READ', 'FAILED'] as const;
export type MessageStatus = (typeof MESSAGE_STATUSES)[number];

export const TEMPLATE_STATUSES = ['DRAFT', 'SUBMITTED', 'APPROVED', 'REJECTED', 'PAUSED'] as const;
export type TemplateStatus = (typeof TEMPLATE_STATUSES)[number];

export const TEMPLATE_CODES = ['SIGN_IN_CODE', 'RECEIPT_ISSUED', 'ANNOUNCEMENT', 'JANAZA_ALERT'] as const;
export type TemplateCode = (typeof TEMPLATE_CODES)[number];

/** Who may read the delivery log and send a failed message again. */
export const MESSAGE_LOG_ROLES: readonly Role[] = ['MAHALLU_ADMIN', 'PRESIDENT', 'SECRETARY', 'TREASURER'];

/** Retries on one channel before falling back to the next, and the waits between them (seconds). */
export const RETRY_DELAYS = [30, 120, 600] as const;

export interface MessageTemplateRow {
  code: TemplateCode;
  channel: MessageChannel;
  lang: 'EN' | 'ML';
  category: 'AUTHENTICATION' | 'UTILITY' | 'MARKETING';
  status: TemplateStatus;
  statusNote: string | null;
  bodyPreview: string;
}

export interface MessageEventRow {
  at: string;
  event: 'QUEUED' | 'ATTEMPT' | 'SENT' | 'DELIVERED' | 'READ' | 'RETRY' | 'FALLBACK' | 'FAILED' | 'REQUEUED';
  channel: MessageChannel | null;
  detail: string | null;
}

export interface MessageRow {
  id: string;
  templateCode: TemplateCode;
  toMasked: string;
  lang: 'EN' | 'ML';
  subject: { type: string; id: string; label: string | null };
  channels: MessageChannel[];
  channel: MessageChannel | null;
  status: MessageStatus;
  attempts: number;
  lastError: string | null;
  createdAt: string;
  sentAt: string | null;
  deliveredAt: string | null;
  events: MessageEventRow[];
  version: number;
}

export interface MessageList {
  rows: MessageRow[];
  total: number;
  page: number;
  size: number;
  /** Count per status for the filters above the list. */
  counts: Partial<Record<MessageStatus, number>>;
}

export const MESSAGE_FILTERS = ['status', 'q', 'page', 'size'] as const;

/** The message sent about a receipt, as it stands now. */
export interface ReceiptNotice {
  status: MessageStatus;
  channel: MessageChannel | null;
  toMasked: string;
}

/** Why no message was queued for a new receipt. */
export const NOTICE_SKIPS = ['NOT_REQUESTED', 'SWITCHED_OFF', 'NO_HOUSE', 'NO_PHONE', 'NO_CONSENT'] as const;
export type NoticeSkip = (typeof NOTICE_SKIPS)[number];
